<template>
  <div ref="searchRef" class="relative" :class="className">
    <!-- Search input -->
    <div class="flex items-center pl-2 border border-gray-300 rounded bg-white">
      <Icon icon-name="SearchIcon" />
      <input
        ref="inputRef"
        type="text"
        :value="searchValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :title="title"
        class="flex-1 h-10 px-3 text-gray-900 placeholder-gray-400 focus:outline-none transition-colors duration-200"
        :class="disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'"
        @input="handleInputChange"
        @keydown="handleInputKeyDown"
      />
    </div>

    <!-- Dropdown -->
    <div
      v-if="showDropdown && isOpen && filteredOptions.length"
      class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto z-20"
    >
      <div
        v-for="option in filteredOptions"
        :key="option.id || option.value"
        role="button"
        tabindex="0"
        class="w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 border-b border-gray-100 last:border-b-0 cursor-pointer"
        @click="handleOptionClick(option)"
        @keydown="handleKeyActivate"
      >
        {{ option.label }}
      </div>
    </div>

    <!-- No results -->
    <div
      v-if="showDropdown && isOpen && searchValue.length && !filteredOptions.length"
      class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
    >
      <div class="px-4 py-3 text-gray-400 text-xs">
        No results found
      </div>
    </div>
  </div>
</template>

<script>
import Icon from './Icon.vue'

export default {
  name: 'Search',

  components: {
    Icon
  },

  props: {
    placeholder: {
      type: String,
      default: 'Search here...'
    },
    value: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    showDropdown: {
      type: Boolean,
      default: false
    },
    className: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      searchValue: this.value,
      isOpen: false
    }
  },

  computed: {
    filteredOptions() {
      if (!this.showDropdown || !this.options.length) return []
      const val = this.searchValue.toLowerCase()
      return this.options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(val) ||
          opt.value.toLowerCase().includes(val)
      )
    }
  },

  watch: {
    value(newVal) {
      this.searchValue = newVal
    }
  },

  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside)
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  },

  methods: {
    handleInputChange(e) {
      const newValue = e.target.value
      this.searchValue = newValue
      this.$emit('change', newValue)

      if (this.showDropdown) {
        this.isOpen = newValue.length > 0
      }
    },

    handleInputKeyDown(e) {
      if (e.key === 'Enter') {
        this.$emit('search', this.searchValue)
        this.isOpen = false
      }
    },

    handleOptionClick(option) {
      this.searchValue = option.label
      this.$emit('change', option.label)
      this.$emit('search', option.value)
      this.isOpen = false
    },

    handleKeyActivate(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.currentTarget.click()
      }
    },

    handleClickOutside(e) {
      if (this.$refs.searchRef && !this.$refs.searchRef.contains(e.target)) {
        this.isOpen = false
      }
    }
  }
}
</script>

<style scoped>
.text-gray-400 {
  color: #9ca3af;
}
</style>
